// src/components/forms/shared/DisplayFieldPresets.ts
// Theme-aware preset configurations for DisplayField variants
import { useTheme, useMediaQuery } from '@mui/material';
import { DisplayFieldProps } from './DisplayField';

/**
 * Hook that returns theme-aware preset configurations for DisplayField variants
 */
export const useDisplayFieldPresets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return {
    /**
     * Standard being details field
     * Used for most fields in ClientDetailsCard and PetDetailsCard
     */
    beingDetails: (): Partial<DisplayFieldProps> => ({
      hideLabel: isMobile,
      labelBackgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[800]
          : theme.palette.grey[400],
      valueFontSize: '2rem',
      valueFontWeight: 500,
      labelFontSize: '1.8rem',
      labelFontWeight: 600,
    }),

    /**
     * Header field with background
     * Used for name/surname fields that need visual distinction
     */
    beingHeader: (): Partial<DisplayFieldProps> => ({
      hideLabel: isMobile,
      labelBackgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[800]
          : theme.palette.grey[400],
      valueFontSize: '2rem',
      valueFontWeight: 500,
      labelFontSize: '1.8rem',
      labelFontWeight: 600,
    }),

    /**
     * Metadata field styling
     * Used for bottom row with ID and timestamps
     */
    metadata: (): Partial<DisplayFieldProps> => ({
      hideColon: true,
      labelBackgroundColor: theme.palette.primary.main,
      labelBackgroundColorMobile:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.main
          : theme.palette.primary.main,
      labelColor: theme.palette.primary.contrastText,
      labelFontSize: '1.8rem',
      labelFontSizeMobile: '1.5rem',
      labelFontWeight: 700,
      valueBackgroundColor: theme.palette.tertiary.main,
      color: theme.palette.tertiary.contrastText,
      valueFontSize: '1.75rem',
      valueFontSizeMobile: '1.5rem',
      valueFontWeight: 600,
      flexDirection: isMobile ? 'column' : 'row',
      labelPadding: '1rem',
      labelPaddingMobile: '0.5rem 0',
      valuePadding: '1rem',
      valuePaddingMobile: '0.5rem 1.5rem',
    }),
  };
};

/**
 * Utility function to merge preset with custom overrides
 * @param preset - Base preset configuration
 * @param overrides - Custom props to override preset
 * @returns Merged configuration
 */
export const mergePresetWithOverrides = (
  preset: Partial<DisplayFieldProps>,
  overrides: Partial<DisplayFieldProps>
): Partial<DisplayFieldProps> => {
  return {
    ...preset,
    ...overrides,
  };
};
